import { useEffect, useRef } from 'react'
import { InputGroupInput } from '@playbooks/ui/components/input-group'
import { InputProps } from '../types'
import { useTranslation } from '../../../contexts/localization/Localization'
import InputGroupWrapper from '../../../components/ui/InputGroupeWrapper'

/**
 * Renders an email input field. Has validation.
 * It checks if the input value is a valid email format
 * @param {InputProps<string>} props - The properties for the email input component, including question data, onChange handler, and invalid state.
 * @returns {JSX.Element} The rendered input component.
 */
export default function EmailInput({ question, onChange, invalid }: InputProps<string>) {
  const { text } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const tidRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Sync input with external value changes, skip when user is actively typing
  useEffect(() => {
    if (inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.value = (question.value as string) ?? ''
    }
  }, [question.value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearTimeout(tidRef.current!)

    const email = e.target.value

    tidRef.current = setTimeout(() => {
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email === '') {
        onChange(email)
      } else {
        onChange(email, text('emailValidationError'))
      }
    }, 200)
  }

  return (
    <InputGroupWrapper>
      <InputGroupInput
        ref={inputRef}
        id={`question-${question.id}`}
        type="email"
        defaultValue={(question.value as string) ?? ''}
        onChange={handleChange}
        aria-invalid={invalid}
        required={question.required}
        className="rounded-md"
      />
    </InputGroupWrapper>
  )
}
